import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';

import { ColDefBuilder, DataTable, Heading, Text } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { type TenantBundleBlocker } from './components/BundleBlockerTooltip';
import {
  TenantPromptTemplateToggle,
  tenantPromptTemplateFragment,
} from './components/TenantPromptTemplateToggle';
import {
  getTenantEnabledBundlesForDepsQuery,
  groupBlockersById,
} from './components/tenant-template-deps.operations';

const tenantPromptTemplatesFragment = graphql(
  `
    fragment TenantPromptTemplates on Tenants @_unmask {
      __typename
      id
      promptTemplates {
        ...TenantPromptTemplate
        prompt_template {
          __typename
          id
          name
          description
          updatedAt
        }
      }
    }
  `,
  [tenantPromptTemplateFragment],
);

const tenantPromptTemplatesQuery = graphql(
  `
    query getTenantPromptTemplates($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        ...TenantPromptTemplates
      }
    }
  `,
  [tenantPromptTemplatesFragment],
);

type TableRow = {
  tenantId: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  promptTemplateId: string;
  templateUpdatedAt: string;
  accessUpdatedAt: string;
  blockedBy: TenantBundleBlocker[];
};

const colDefs = new ColDefBuilder<TableRow>()
  .detailsLink((r) => `/admin/prompts/${r.promptTemplateId}`)
  .defaultSortable('name')
  .defaultSortable('description', {
    cellRenderer: (row) =>
      row.description ? (
        <div className="max-w-md truncate" title={row.description}>
          {row.description}
        </div>
      ) : (
        <Text color="disabled">—</Text>
      ),
  })
  .defaultSortable('isEnabled', {
    label: 'Is Enabled',
    cellRenderer: (row) => {
      return (
        <TenantPromptTemplateToggle
          tenantId={row.tenantId}
          promptTemplateId={row.promptTemplateId}
          name={row.name}
          blockedBy={row.blockedBy}
        />
      );
    },
  })
  .dateSortable('templateUpdatedAt', 'Template Updated At')
  .dateSortable('accessUpdatedAt', 'Access Updated At')
  .build();

export const AdminTenantPromptTemplatesPage: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;

  const {
    data: { tenantsByPk: tenant },
  } = useSuspenseQuery(tenantPromptTemplatesQuery, {
    variables: {
      tenantId,
    },
  });

  const {
    data: { tenantTemplateBundles: enabledBundles },
  } = useSuspenseQuery(getTenantEnabledBundlesForDepsQuery, {
    variables: {
      tenantId,
    },
  });

  const blockersByPromptTemplateId = useMemo(
    () =>
      groupBlockersById(
        enabledBundles,
        (ttb) => ttb.llm_model_prompt_template.promptTemplateId,
      ),
    [enabledBundles],
  );

  const rows = useMemo(
    () =>
      (tenant?.promptTemplates ?? []).map<TableRow>((template) => ({
        tenantId,
        isEnabled: template.isEnabled,
        name: template.prompt_template.name,
        description: template.prompt_template.description,
        promptTemplateId: template.promptTemplateId,
        templateUpdatedAt: template.prompt_template.updatedAt,
        accessUpdatedAt: template.updatedAt,
        blockedBy:
          blockersByPromptTemplateId.get(template.promptTemplateId) ?? [],
      })),
    [tenant?.promptTemplates, tenantId, blockersByPromptTemplateId],
  );

  return (
    <div className="grid gap-2">
      <div>
        <Heading size="2xl" weight="semibold">
          Prompt Templates
        </Heading>
        <Text>
          Control which prompt templates this organization will have access to
        </Text>
      </div>
      <div className="w-full overflow-hidden">
        <DataTable
          data={rows}
          columns={colDefs}
          isPaginated={false}
          enableGlobalSearch
          initialSortingState={[
            {
              id: 'templateUpdatedAt',
              desc: true,
            },
          ]}
        />
      </div>
    </div>
  );
};
